import type { FolderZipReportQuery } from "@/graphql/folders/folders.hooks";

import { useCallback, useMemo } from "react";

import { useFolderZipReportLazyQuery } from "@/graphql/folders/folders.hooks";

type TFolderZipReport = Omit<
  NonNullable<FolderZipReportQuery["folderZipReport"]>,
  "__typename"
> | null;

interface IUseFolderZipReportReturnedValue {
  fetchFolderZipReport: (folderId: number) => Promise<TFolderZipReport>;
  folderZipReport?: TFolderZipReport;
  loading: boolean;
}

// Lets the download modal check, before the user does anything, whether an archive already
// exists (or is still being generated) for the folder — so it can show "download" or
// "come back later" instead of always starting a fresh generation.
const useFolderZipReport = (): IUseFolderZipReportReturnedValue => {
  const [fetch, { data, loading }] = useFolderZipReportLazyQuery({
    fetchPolicy: "network-only",
  });

  const fetchFolderZipReport = useCallback(
    async (folderId: number) => {
      const { data: result } = await fetch({
        variables: { folderId },
        context: { errorType: "local" },
      });
      return result?.folderZipReport ?? null;
    },
    [fetch]
  );

  return useMemo(
    () => ({
      fetchFolderZipReport,
      folderZipReport: data?.folderZipReport,
      loading,
    }),
    [fetchFolderZipReport, data, loading]
  );
};

export default useFolderZipReport;
